import * as Blockly from 'blockly/core';
import { useBlockModalIrStore } from '@/stores/useBlockModalIrStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';
import type { IrSignalData } from '@/(routers)/boards/editor/block/modal/IrSignal';

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 40;
const PADDING = 12;

export class FieldIrSignal extends Blockly.Field<string> {
    private signals_: IrSignalData[];
    private labelEl_: SVGTextElement | null = null;

    static override fromJson(options: Blockly.FieldConfig & { signals?: IrSignalData[] }): FieldIrSignal {
        return new FieldIrSignal(options.signals ?? []);
    }

    constructor(signals: IrSignalData[]) {
        super(JSON.stringify(signals));
        this.signals_ = signals;
        this.SERIALIZABLE = true;
    }

    private getSignalDisplayText_(): string {
        if (this.signals_.length === 0) return '選択した信号';
        if (this.signals_.length === 1) return this.signals_[0].name;
        return `${this.signals_[0].name} +${this.signals_.length - 1}`;
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: PADDING,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': '500',
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#e65100';
        const fillColour = this.signals_.length === 0 ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_ || !this.labelEl_) return;

        this.labelEl_.textContent = this.getSignalDisplayText_();

        let textWidth = FIELD_MIN_WIDTH;
        try {
            const bbox = this.labelEl_.getBBox();
            if (bbox.width > 0) {
                textWidth = Math.max(FIELD_MIN_WIDTH, PADDING + bbox.width + PADDING);
            }
        } catch {
            const text = this.getSignalDisplayText_();
            let w = 0;
            for (const char of text) {
                w += char.charCodeAt(0) > 127 ? 14 : 8;
            }
            textWidth = Math.max(FIELD_MIN_WIDTH, PADDING + w + PADDING);
        }

        this.borderRect_?.setAttribute('width', String(textWidth));
        this.size_ = new Blockly.utils.Size(textWidth, FIELD_HEIGHT);
        this.applyStyle_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setSignals(signals: IrSignalData[]): void {
        this.signals_ = signals;
        const json = JSON.stringify(signals);
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', json));
        }
    }

    public getSignals(): IrSignalData[] {
        return this.signals_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            this.signals_ = JSON.parse(newValue) as IrSignalData[];
        } catch {
            this.signals_ = [];
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return JSON.stringify(this.signals_);
    }

    override getSize(): Blockly.utils.Size {
        if (this.labelEl_) {
            try {
                const bbox = this.labelEl_.getBBox();
                if (bbox.width > 0) {
                    const width = Math.max(FIELD_MIN_WIDTH, PADDING + bbox.width + PADDING);
                    this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
                    return this.size_;
                }
            } catch {
                // fallthrough
            }
        }
        return this.size_ ?? new Blockly.utils.Size(FIELD_MIN_WIDTH, FIELD_HEIGHT);
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalIrStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef(this);
        openModal('ir');
    }
}
